import type { User } from 'firebase/auth';

export type DevGlobalDividendCalendarRefreshDiagnostics = {
    status?: string;
    providerRequestCount?: number;
    rowsFetched?: number;
    normalizedEvents?: number;
    docsWritten?: number;
    errors?: unknown[];
    [key: string]: unknown;
};

const getFallbackEndpointBase = () => (
    (typeof window !== 'undefined' ? (window as unknown as { __FIREAPP_DEV_REFRESH_GLOBAL_DIVIDEND_CALENDAR_URL__?: string }).__FIREAPP_DEV_REFRESH_GLOBAL_DIVIDEND_CALENDAR_URL__ : '') ||
    (typeof process !== 'undefined' ? process.env.VITE_DEV_REFRESH_GLOBAL_DIVIDEND_CALENDAR_URL : '') ||
    (typeof process !== 'undefined' ? process.env.VITE_DIVIDEND_ENDPOINT_BASE_URL : '') ||
    (typeof process !== 'undefined' ? process.env.VITE_MARKET_DATA_ENDPOINT_BASE_URL : '') ||
    (typeof process !== 'undefined' ? process.env.VITE_QUOTE_ENDPOINT_BASE_URL : '') ||
    ''
);

const DEV_REFRESH_FUNCTION_NAME = 'devRefreshGlobalDividendCalendar';

export const getDevGlobalDividendCalendarRefreshUrl = (endpointBase = getFallbackEndpointBase()) => {
    if (!endpointBase) {
        return `/${DEV_REFRESH_FUNCTION_NAME}`;
    }

    try {
        const url = new URL(endpointBase);
        if (url.pathname.replace(/\/+$/, '').endsWith(`/${DEV_REFRESH_FUNCTION_NAME}`)) {
            return url.toString();
        }

        if (url.hostname.endsWith('.a.run.app') && url.hostname.includes('-')) {
            const hostParts = url.hostname.split('-');
            hostParts[0] = DEV_REFRESH_FUNCTION_NAME.toLowerCase();
            url.hostname = hostParts.join('-');
            url.pathname = '/';
            url.search = '';
            return url.toString();
        }
    } catch {
        // Fallback to path-based URL composition.
    }

    return `${endpointBase.replace(/\/+$/, '')}/${DEV_REFRESH_FUNCTION_NAME}`;
};

export const refreshGlobalDividendCalendarForDev = async (
    user: Pick<User, 'getIdToken'>,
    endpointBase?: string,
): Promise<DevGlobalDividendCalendarRefreshDiagnostics> => {
    const token = await user.getIdToken(true);
    const response = await fetch(getDevGlobalDividendCalendarRefreshUrl(endpointBase), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ force: false }),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        const message = typeof payload?.error === 'string'
            ? payload.error
            : `Global dividend calendar refresh failed (${response.status}).`;
        throw new Error(message);
    }

    return payload as DevGlobalDividendCalendarRefreshDiagnostics;
};
